import React, { useContext, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import styles from './Navigation.module.css';

/**
 * Focused navigation for SMB voice-ad wedge.
 * Primary path: Create Ad. Legacy tools stay admin-only or unlinked.
 */
const Navigation = () => {
  const { token, user, logout } = useContext(AuthContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');
  const isAuthed = !!(user || token);

  // "Create Ad" is intentionally not a separate nav link — the "Make a voice ad"
  // CTA button below already points to /create and is the one link to it.
  const primary = [
    { to: '/', label: 'Home' },
    { to: '/discover', label: 'Examples' },
  ];

  const account = isAuthed
    ? [
        { to: '/profile', label: 'My Ads' },
        { to: '/uploads', label: 'Uploads' },
      ]
    : [];

  const admin = isAdmin
    ? [
        { to: '/admin', label: 'Admin Hub' },
        { to: '/admin/streams-moderation', label: 'Moderation' },
        { to: '/admin/metrics', label: 'Metrics' },
        { to: '/quickstart', label: 'Legacy Quick Start' },
        { to: '/adstudio', label: 'Legacy Ad Studio' },
      ]
    : [];

  const NavItem = ({ to, label, onClick, className = '' }) => (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''} ${className}`.trim()}
    >
      <span>{label}</span>
    </NavLink>
  );

  const MobileNavItem = ({ to, label, onClick }) => (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => `${styles.mobileNavItem} ${isActive ? styles.mobileNavItemActive : ''}`}
    >
      <span>{label}</span>
    </NavLink>
  );

  return (
    <>
      <nav className={styles.desktopNav}>
        <div className={styles.navContainer}>
          <Link to="/" className={styles.navLogo}>
            <span className={styles.navLogoIcon}>🔊</span>
            <span className={styles.navLogoText}>Loudest.ai</span>
          </Link>

          <div className={styles.navSection}>
            {primary.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </div>

          <div className={styles.navSection}>
            <Link to="/create" className={styles.createButton}>
              Make a voice ad
            </Link>
          </div>

          <div className={`${styles.navSection} ${styles.navSectionRight}`}>
            {isAuthed ? (
              <div className={styles.userMenu}>
                <button
                  type="button"
                  className={styles.userMenuTrigger}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-expanded={userMenuOpen}
                >
                  <span className="user-email">{user?.email || 'Account'}</span>
                  <ChevronDown size={16} />
                </button>

                {userMenuOpen && (
                  <div className={styles.userDropdown}>
                    {account.map((item) => (
                      <NavItem
                        key={item.to}
                        {...item}
                        onClick={() => setUserMenuOpen(false)}
                      />
                    ))}
                    {admin.map((item) => (
                      <NavItem
                        key={item.to}
                        {...item}
                        onClick={() => setUserMenuOpen(false)}
                      />
                    ))}
                    <button type="button" className={styles.logoutBtn} onClick={logout}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="btn btn-ghost">Login</Link>
                <Link to="/register" className="btn btn-primary">Sign Up</Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      <nav className={styles.mobileNav}>
        <div className={styles.mobileNavHeader}>
          <Link to="/" className={styles.navLogo}>
            <span className={styles.navLogoIcon}>🔊</span>
            <span className={styles.navLogoText}>Loudest.ai</span>
          </Link>

          <button
            type="button"
            className={styles.mobileMenuToggle}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className={styles.mobileMenu}>
            <div className={styles.mobileMenuSection}>
              <Link
                to="/create"
                className={styles.createButton}
                onClick={() => setMobileMenuOpen(false)}
              >
                Make a voice ad
              </Link>
            </div>

            <div className={styles.mobileMenuSection}>
              <h3 className={styles.mobileMenuSectionTitle}>Main</h3>
              {primary.map((item) => (
                <MobileNavItem
                  key={item.to}
                  {...item}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </div>

            {isAuthed && (
              <div className={styles.mobileMenuSection}>
                <h3 className={styles.mobileMenuSectionTitle}>Account</h3>
                {account.map((item) => (
                  <MobileNavItem
                    key={item.to}
                    {...item}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                ))}
              </div>
            )}

            {isAdmin && (
              <div className={styles.mobileMenuSection}>
                <h3 className={styles.mobileMenuSectionTitle}>Admin / Legacy</h3>
                {admin.map((item) => (
                  <MobileNavItem
                    key={item.to}
                    {...item}
                    onClick={() => setMobileMenuOpen(false)}
                  />
                ))}
              </div>
            )}

            {isAuthed ? (
              <button type="button" className={styles.logoutBtn} onClick={logout}>
                Logout
              </button>
            ) : (
              <div className={styles.mobileMenuSection}>
                <Link to="/login" className="btn btn-secondary" onClick={() => setMobileMenuOpen(false)}>
                  Login
                </Link>
                <Link to="/register" className="btn btn-primary" onClick={() => setMobileMenuOpen(false)}>
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default Navigation;
